import * as yup from 'yup'
import { BranchService } from '.'
import { LIMIT, STATUS, FILE_REF_TYPE } from 'types'
import { Branch } from 'features/Branch'

import type { BranchForForm, OptionProps } from 'types'

type Context = yup.TestContext & { type?: string }

export const initialBranchForm: BranchForForm = {
  currentState: 'code',
  isActive: true,
  companyId: '',
  name: '',
  countryCode: '+66',
  phoneNumber: '',
  // branch address section
  isSameAsCompany: false,
  address: '',
  country: {
    id: '',
  },
  province: {
    id: '',
  },
  district: {
    id: '',
  },
  subDistrict: {
    id: '',
  },
  postalCode: '',
  email: '',
  location: '',
  detail: '',
  // branch contact point section
  contactName: '',
  contactCountryCode: '+66',
  contactPhoneNumber: '',
}

export const initialBranchFormUpdate = {
  ...initialBranchForm,
  code: '',
  id: '',
}

export const initialBranchDetail: Branch = {
  code: '',
  companyId: '',
  companyName: '',
  contactName: '',
  createBy: '',
  createdAt: '',
  deleteBy: '',
  deletedAt: '',
  detail: '',
  id: '',
  isActive: false,
  isSameAsCompany: false,
  location: '',
  mbsClientId: '',
  name: '',
  updatedAt: '',
  updatedBy: '',
  addresses: [
    {
      id: '',
      refId: '',
      refType: FILE_REF_TYPE.BRANCH,
      type: '',
      address: '',
      countryEn: '',
      countryTh: '',
      provinceEn: '',
      provinceTh: '',
      districtEn: '',
      districtTh: '',
      subDistrictEn: '',
      subDistrictTh: '',
      postalCode: '',
    },
  ],
  contacts: [],
  product: [],
}

const isPhoneOffice = (phone: string) =>
  phone?.startsWith('02') && phone?.replace('-', '').length !== 9
const isPhoneNormal = (phone: string) =>
  !phone?.startsWith('02') && phone?.replace('-', '').length !== 10

export const schemaBranchForm = yup.object().shape({
  status: yup.boolean(),
  companyId: yup.string().required('company:nameRequired.label'),
  name: yup.string().max(100).required('branch:nameRequired.label'),
  countryCode: yup.string(),
  phoneNumber: yup
    .string()
    .max(15)
    .test('phoneNumber', async (phoneNumber, context: Context) => {
      const { path, resolve, createError } = context

      if (
        phoneNumber &&
        (isPhoneOffice(phoneNumber) || isPhoneNormal(phoneNumber))
      ) {
        return createError({
          path,
          message: 'common:phoneInvalid.label',
        })
      }

      return resolve(true)
    }),
  isSameAsCompany: yup.boolean(),
  address: yup.string().max(100).required('common:addressRequired.label'),
  country: yup.object().shape({
    id: yup.string().required('common:countryRequired.label'),
    countryTh: yup.string().required('common:countryRequired.label'),
    countryEn: yup.string().required('common:countryRequired.label'),
  }),
  province: yup.object().shape({
    id: yup.string().required('common:provinceRequired.label'),
    provinceTh: yup.string().required('common:provinceRequired.label'),
    provinceEn: yup.string().required('common:provinceRequired.label'),
  }),
  district: yup.object().shape({
    id: yup.string().required('common:districtRequired.label'),
    districtTh: yup.string().required('common:districtRequired.label'),
    districtEn: yup.string().required('common:districtRequired.label'),
  }),
  subDistrict: yup.object().shape({
    id: yup.string().required('common:subdistrictRequired.label'),
    subDistrictTh: yup.string().required('common:subdistrictRequired.label'),
    subDistrictEn: yup.string().required('common:subdistrictRequired.label'),
  }),
  postalCode: yup.string().max(8).required('common:postalCodeRequired.label'),
  email: yup
    .string()
    .max(50)
    .required('common:emailRequired.label')
    .email('common:emailInvalid.label'),
  location: yup.string(),
  detail: yup.string().max(1000),

  contactName: yup
    .string()
    .max(50)
    .required('common:contactNameRequired.label'),
  contactCountryCode: yup
    .string()
    .required('common:contactPhoneRequired.label'),
  contactPhoneNumber: yup
    .string()
    .max(15)
    .required('common:contactPhoneRequired.label')
    .test(
      'contactPhoneNumber',
      async (contactPhoneNumber, context: Context) => {
        const { path, resolve, createError } = context

        if (
          contactPhoneNumber &&
          (isPhoneOffice(contactPhoneNumber) ||
            isPhoneNormal(contactPhoneNumber))
        ) {
          return createError({
            path,
            message: 'common:contactPhoneInvalid.label',
          })
        }

        return resolve(true)
      },
    ),
})

export const schemaBranchFormUpdateOnly = yup.object().shape({
  code: yup
    .string()
    .max(8)
    .required('branch:idRequired.label')
    .test('code', async (code, context: Context) => {
      const { path, resolve, createError, parent, type } = context

      if (!code) {
        return resolve(true)
      }

      if (
        parent?.currentState === type ||
        parent?.currentState === 'onSubmit'
      ) {
        const pathname = window.location.pathname
        const matchedBranchId = pathname.match(
          new RegExp(/branch\/(.*?)\/edit/),
        )
        const branchId = matchedBranchId && matchedBranchId[1]

        const response = await BranchService.findAll({
          code: code.trim(),
          _limit: LIMIT.NO_LIMIT,
          isActive: STATUS.ALL,
        })
        const branch = response.body.find(
          (_branch) => _branch.code.toLowerCase() === code.trim().toLowerCase(),
        )

        if (!branch) {
          return resolve(true)
        }

        if (branchId === `${branch.id}`) {
          return resolve(true)
        }

        return createError({
          path,
          message: 'branch:idDuplicate.label',
        })
      }

      return resolve(true)
    }),
})

export const schemaBranchFormUpdate = schemaBranchForm.concat(
  schemaBranchFormUpdateOnly,
)

export const findSelected = (
  options?: OptionProps[],
  key?: string,
): OptionProps | null => {
  if (!options) {
    return null
  }

  return options.find((item: OptionProps) => item.key === key) || null
}
